import {PRODUCT_REVIEW_TAB_INDEX} from '../../productReviewConstants'
import {toNumberOrRaw} from '../ui/productReviewUtils'

export const PRODUCT_REVIEW_RENDERERS = Object.freeze({
  TEXT: 'text',
  TEXTAREA: 'textarea',
  TOGGLE: 'toggle',
  DATE: 'date',
})

/**
 * @typedef {'text' | 'textarea' | 'toggle' | 'date'} ProductReviewRenderer
 */

/**
 * @typedef {Object} ProductReviewFieldConfig
 * @property {string} key Field key used to read value from source object.
 * @property {string} source Product data source key (for example: baseData, additionalInfo).
 * @property {string} label Display label for the field.
 * @property {boolean} editable Whether field is editable.
 * @property {boolean} required Whether field is required.
 * @property {ProductReviewRenderer | string} renderer Renderer type for the field.
 * @property {boolean} [localized] When true, field values are keyed by language code.
 * @property {(value: unknown) => unknown} [parseValue] Optional transform applied to the raw edit-control value before it's stored (for example, coercing numeric input).
 * @property {Object} [requiredWhen] Conditional requirement: field is only required if this condition is met.
 * @property {string} [requiredWhen.field] The field to check (e.g., 'additionalInfo.isNewProduct').
 * @property {unknown} [requiredWhen.value] The value that triggers the requirement.
 * @property {Object} [editableWhen] Conditional editability: field is only editable if this condition is met.
 * @property {string} [editableWhen.field] The field to check (e.g., 'additionalInfo.isNewProduct').
 * @property {unknown} [editableWhen.value] The value that enables editing.
 */

/**
 * @typedef {Object} ProductReviewSectionConfig
 * @property {string} id Unique section id.
 * @property {string} [title] Optional section title.
 * @property {ProductReviewFieldConfig[]} fields Section fields.
 * @property {boolean} [multiLanguage] When true, render section once per language with language heading.
 */

/**
 * Base data section configuration used by product review renderers.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_BASE_DATA_SECTION = {
  id: 'base-data',
  title: 'Product Information',
  fields: [
    {
      key: 'name',
      source: 'baseData',
      label: 'Display Name',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: false,
    },
    {
      key: 'status',
      source: 'baseData',
      label: 'Status',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'productSize',
      source: 'baseData',
      label: 'Product Size',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'segments',
      source: 'baseData',
      label: 'Store Segment(s)',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXTAREA,
    },
    {
      key: 'skuNumber',
      source: 'baseData',
      label: 'SKU Number',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'barCode',
      source: 'baseData',
      label: 'Bar Code',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'routeTypeId',
      source: 'baseData',
      label: 'Route Type ID',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'productLine',
      source: 'baseData',
      label: 'Product Line',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'productGroup',
      source: 'baseData',
      label: 'Product Group',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'productContainer',
      source: 'baseData',
      label: 'Product Container',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'saleUom',
      source: 'baseData',
      label: 'Sale Unit Name',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'allowedInMultiplesOf',
      source: 'additionalInfo',
      label: 'Multiple Selling Units',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'suggestedSellingPrice',
      source: 'additionalInfo',
      label: 'Suggested Product Selling Price',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'sorting',
      source: 'baseData',
      label: 'Sorting',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'showOnEcommerce',
      source: 'baseData',
      label: 'Show on e-commerce',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TOGGLE,
    },
    {
      key: 'showOnEngagement',
      source: 'baseData',
      label: 'Show on engagement',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TOGGLE,
    },
    {
      key: 'sizeId',
      source: 'baseData',
      label: 'Display Product Size Id',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'productTypeId',
      source: 'baseData',
      label: 'Display Product Type Id',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
  ],
}

/**
 * Returns and deposits section configuration used by product review renderers.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_RETURNS_AND_DEPOSITS_SECTION = {
  id: 'returns-deposits',
  title: 'Returns & Deposits',
  fields: [
    {
      key: 'isReturnable',
      source: 'baseData',
      label: 'Is returnable',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TOGGLE,
    },
    {
      key: 'isDepositable',
      source: 'baseData',
      label: 'Has deposit',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TOGGLE,
    },
    {
      key: 'description',
      source: 'baseData',
      label: 'Description',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXTAREA,
    },
    {
      key: 'businessDescription',
      source: 'baseData',
      label: 'Business Description',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXTAREA,
    },
  ],
}

/**
 * Product description section configuration with multilanguage support.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_DESCRIPTION_SECTION = {
  id: 'product-description',
  multiLanguage: true,
  fields: [
    {
      key: 'productDescription',
      source: 'baseData',
      label: 'Product Description',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'sizeByLocale',
      source: 'baseData',
      label: 'Display Product Size',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'productTypeByLocale',
      source: 'baseData',
      label: 'Display Product Type',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
  ],
}

/**
 * Display information section configuration with multilanguage support.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_DISPLAY_INFORMATION_SECTION = {
  id: 'display-information',
  multiLanguage: true,
  fields: [
    {
      key: 'displayName',
      source: 'display',
      label: 'Display Name',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'displaySize',
      source: 'display',
      label: 'Display Size',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'flavor',
      source: 'display',
      label: 'Product Flavor',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'shortDescription',
      source: 'display',
      label: 'Short Description',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'keywords',
      source: 'display',
      label: 'Keywords',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'longDescription',
      source: 'display',
      label: 'Long Description',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
  ],
}

/**
 * Brand information section configuration used by product review renderers.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_BRAND_INFORMATION_SECTION = {
  id: 'brand-information',
  fields: [
    {
      key: 'brandId',
      source: 'brandInfo',
      label: 'Brand ID',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'displayBrandId',
      source: 'brandInfo',
      label: 'Display Brand ID',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'brandGroupName',
      source: 'brandInfo',
      label: 'Brand Group Name',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'subBrandId',
      source: 'brandInfo',
      label: 'Sub Brand ID',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
  ],
}

/**
 * Brand multilingual section configuration used by product review renderers.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_BRAND_MULTILANGUAGE_SECTION = {
  id: 'brand-information-multilanguage',
  multiLanguage: true,
  fields: [
    {
      key: 'brand',
      source: 'brandInfo',
      label: 'Product Brand',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'displayBrand',
      source: 'brandInfo',
      label: 'Display Brand',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
    {
      key: 'subBrand',
      source: 'brandInfo',
      label: 'Product Sub brand',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
  ],
}

/**
 * Category information section configuration used by product review renderers.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_CATEGORY_SECTION = {
  id: 'category-information',
  fields: [
    {
      key: 'erpCategory',
      source: 'category',
      label: 'ERP Category',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'erpCategoryId',
      source: 'category',
      label: 'ERP Category ID',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'categoryType',
      source: 'category',
      label: 'Category Type',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'displayCategoryId',
      source: 'category',
      label: 'Display Category ID',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
  ],
}

/**
 * Brand multilingual section configuration used by product review renderers.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_CATEGORY_MULTILANGUAGE_SECTION = {
  id: 'category-information-multilanguage',
  multiLanguage: true,
  fields: [
    {
      key: 'displayCategory',
      source: 'category',
      label: 'Display Category',
      editable: true,
      required: true,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      localized: true,
    },
  ],
}

/**
 * Additional information section configuration used by product review renderers.
 * @type {ProductReviewSectionConfig}
 */
export const PRODUCT_REVIEW_ADDITIONAL_INFORMATION_SECTION = {
  id: 'additional-information',
  fields: [
    {
      key: 'isNewProduct',
      source: 'additionalInfo',
      label: 'New SKU tag',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TOGGLE,
    },
    {
      key: 'skuStartDate',
      source: 'additionalInfo',
      label: 'New SKU Start Date',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.DATE,
      dependentDate: 'skuEndDate',
      requiredWhen: { field: 'additionalInfo.isNewProduct', value: true },
      editableWhen: { field: 'additionalInfo.isNewProduct', value: true },
    },
    {
      key: 'skuEndDate',
      source: 'additionalInfo',
      label: 'New SKU End Date',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.DATE,
      dependentDate: 'skuStartDate',
      requiredWhen: { field: 'additionalInfo.isNewProduct', value: true },
      editableWhen: { field: 'additionalInfo.isNewProduct', value: true },
    },
    {
      key: 'referenceSkuId',
      source: 'additionalInfo',
      label: 'Reference ID',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'displaySizeId',
      source: 'additionalInfo',
      label: 'Display Size ID',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
      parseValue: toNumberOrRaw,
    },
    {
      key: 'displayProductSize',
      source: 'additionalInfo',
      label: 'Product Size',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'productCode2',
      source: 'additionalInfo',
      label: 'Product Code',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'gtmuId',
      source: 'additionalInfo',
      label: 'GMTU',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'salesOrg',
      source: 'additionalInfo',
      label: 'Sales Org',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'packageName',
      source: 'additionalInfo',
      label: 'Package Name',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'packageCode',
      source: 'additionalInfo',
      label: 'Package',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'distributionChannel',
      source: 'additionalInfo',
      label: 'Distributional Channel',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'displayOrder',
      source: 'additionalInfo',
      label: 'Display Order',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'minimumOrderQuantity',
      source: 'additionalInfo',
      label: 'Minimum Order Quantity',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'maximumOrderQuantity',
      source: 'additionalInfo',
      label: 'Maximum Order Quantity',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'unitMultiplier',
      source: 'additionalInfo',
      label: 'Unit Multiplier',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'wishlist',
      source: 'additionalInfo',
      label: 'Wishlist',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'rewardPoints',
      source: 'additionalInfo',
      label: 'Report Points',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'allowedInMultiplesOf',
      source: 'additionalInfo',
      label: 'Allowed in multiples of',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'returnPriceForBaseUnit',
      source: 'additionalInfo',
      label: 'Return Price For Base Unit',
      editable: true,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
    {
      key: 'saleUnitConversionToBase',
      source: 'additionalInfo',
      label: 'Sale Unit Conversion To Base',
      editable: false,
      required: false,
      renderer: PRODUCT_REVIEW_RENDERERS.TEXT,
    },
  ],
}

/**
 * Maps each Product Review tab index to the section configurations rendered on that tab.
 * Used to scope required-field validation to only the fields visible on the current tab.
 * @type {Object.<number, ProductReviewSectionConfig[]>}
 */
export const PRODUCT_REVIEW_TAB_SECTIONS = {
  [PRODUCT_REVIEW_TAB_INDEX.BASE_DATA]: [
    PRODUCT_REVIEW_BASE_DATA_SECTION,
    PRODUCT_REVIEW_DESCRIPTION_SECTION,
    PRODUCT_REVIEW_RETURNS_AND_DEPOSITS_SECTION,
    PRODUCT_REVIEW_ADDITIONAL_INFORMATION_SECTION,
  ],
  [PRODUCT_REVIEW_TAB_INDEX.MEDIA]: [],
  [PRODUCT_REVIEW_TAB_INDEX.DISPLAY]: [PRODUCT_REVIEW_DISPLAY_INFORMATION_SECTION],
  [PRODUCT_REVIEW_TAB_INDEX.BRAND_AND_CATEGORY]: [
    PRODUCT_REVIEW_BRAND_INFORMATION_SECTION,
    PRODUCT_REVIEW_BRAND_MULTILANGUAGE_SECTION,
    PRODUCT_REVIEW_CATEGORY_SECTION,
    PRODUCT_REVIEW_CATEGORY_MULTILANGUAGE_SECTION,
  ],
  [PRODUCT_REVIEW_TAB_INDEX.TAXES_AND_SALES]: [],
}
